import React, { useState, useEffect, useRef } from 'react';
import { compose, } from 'recompose';
import { withStyles } from '@material-ui/core/styles';
import {
  Divider,
  DialogContent,
  Button,
  Dialog,
  Typography,
} from '@material-ui/core';
import { LoadingOverlayWrapper } from 'components/layout';
import CloseableDialogTitle from 'components/layout/CloseableDialogTitle';
import 'components/underwrite/acquisition/import/importOpportunities/LocationVerification/LocationVerificationModal.css';
import {
  LocationVerificationMap,
  LocationVerificationSearchBox,
  LocationVerificationGrid,
  LocationVerificationTextField,
  LocationVerificationCreatePanel
} from 'components/underwrite/acquisition/import/importOpportunities/LocationVerification';
import { SearchPropMasterWithLocation } from 'api/revue/import';
import { useJsApiLoader } from "@react-google-maps/api";
import { validateLatAndLong } from 'api/revue/import';
import { googleMapsApiKey } from "index";
import { sendClientLog } from 'services/logService';

const usePrevious = value => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const useCompare = val => {
  const prevVal = usePrevious(val);
  return prevVal !== val && prevVal != null;
}

const scriptLibraries = ['geometry', 'drawing', 'places'];

const LocationVerificationModal = ({ geoValidationDto, updateLocation, classes, open, onClose, disableBackdropClick, jitCreationType }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey,
    libraries: scriptLibraries,
    version: '3.64'
  });
  const [createModeEnabled, setCreateModeEnabled] = useState(false);
  const [pmSearchIsLoading, setPmSearchIsLoading] = useState(false);
  const [searchBoxAddress, setSearchBoxAddress] = useState(`${geoValidationDto.street}, ${geoValidationDto.zipCode}`);
  const [currentGeoResultDto, setCurrentGeoResultDto] = useState(geoValidationDto.geoResultDto);
  const { propertyMasterResult } = currentGeoResultDto;
  const effectNameRef = useRef("notify-change");

  useEffect(() => {
    init()
  }, []);
  
  const init = async () => {
    effectNameRef.current = effectNameRef.current === "notify-change" ? "notify-change-alt" : "notify-change";//same effect, but triggers when address keeps changing
    const prospectLocationVerified = geoValidationDto.locationVerified; //override lat/lng of locationVerification fields with prospects lat/lng if locationVerified is true (user has updated the location so show that new location instead of PM lat/lng)
    let latOverride = prospectLocationVerified ? geoValidationDto.latitude : geoValidationDto.geoResultDto.propertyMasterResult.latitude;
    let lngOverride = prospectLocationVerified ? geoValidationDto.longitude : geoValidationDto.geoResultDto.propertyMasterResult.longitude;
    const result = await validateLatAndLong([{ lat: latOverride, long: lngOverride }])
    const isLatLngValid = result[0].isValid;
    setCurrentGeoResultDto({
      ...geoValidationDto.geoResultDto,
      isLatLngValid,
      propertyMasterResult: {
        ...geoValidationDto.geoResultDto.propertyMasterResult,
        latitude: latOverride,
        longitude: lngOverride
      }
    });
  }

  const hasLatitudeChanged = useCompare(propertyMasterResult.latitude);
  const hasLongitudeChanged = useCompare(propertyMasterResult.longitude);

  const updateLatLng = async (lat, long) => {
    const result = await validateLatAndLong([{ lat, long }])
    const isLatLngValid = result[0].isValid;
    setCurrentGeoResultDto({
      ...currentGeoResultDto,
      isLatLngValid,
      propertyMasterResult: {
        ...propertyMasterResult,
        latitude: lat,
        longitude: long,
        maintainZoom: true
      }
    })
  }

  const searchWithPm = (req, searchBoxAddress) => {
    setPmSearchIsLoading(true);
    if (searchBoxAddress) {
      setSearchBoxAddress(searchBoxAddress);
    }
    SearchPropMasterWithLocation(req).then(res => {
      console.log(res);
      setPmSearchIsLoading(false);
      setCurrentGeoResultDto({
        ...res,
        isLatLngValid: true
      });
    }).catch(err => {
      console.log(err);
      setPmSearchIsLoading(false);
    });
  }

  const handleSaveLocation = (res) => {
    let geoResultDto;
    if (res && res.propertyMasterResult) {
      geoResultDto = {
        ...res,
        propertyMasterResult: {
          ...res.propertyMasterResult,
          maintainZoom: false
        }
      }
    } else {
      geoResultDto = {
        ...currentGeoResultDto,
        propertyMasterResult: {
          ...propertyMasterResult,
          maintainZoom: false
        }
      }
    }
    updateLocation(geoResultDto);
    sendClientLog('Info', `Import - User clicked Use Location in Location Verification for address ${propertyMasterResult.cleanAddress}, PM ID ${propertyMasterResult.asgPropID}`);
    onClose();
  }

  const handleClose = (e, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') return; // updated handling for disableBackdropClick
    onClose();
  }

  if (!isLoaded) return null;
  return (
    <Dialog open={open} onClose={handleClose} maxWidth={"lg"} classes={{paper: classes.dialogPaper}}>
      <CloseableDialogTitle onClose={onClose}>
        <Typography variant='h6'>{`Location Verification (${geoValidationDto.street}, ${geoValidationDto.zipCode})`}</Typography>
      </CloseableDialogTitle>
      <Divider />
      <div style={{position: 'relative', minHeight: 0, height: '100%'}}>
        <DialogContent style={{ height: '100%', display: 'flex', flexDirection: 'column'}}>
          <div style={{ display: 'flex', marginBottom: 8, justifyContent: 'space-between'}}>
            <LocationVerificationSearchBox searchWithPm={searchWithPm} initialValue={searchBoxAddress} pmSearchIsLoading={pmSearchIsLoading} />
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setCreateModeEnabled(true)}
              disabled={pmSearchIsLoading}
            >
              Create Property
            </Button>
          </div>
          <LoadingOverlayWrapper
            active={pmSearchIsLoading}
            message={'Searching Prop Master...'}
            style={{minHeight: 0, flexGrow: 1}}
          >
          <div className={classes.controlContainer}>
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <div className={classes.fieldContainer} style={(!currentGeoResultDto.passedValidation && !currentGeoResultDto.onlyMissingLatLong) ? { backgroundColor: '#ffc1c1' } : {}}>
                <LocationVerificationTextField label='Status'
                  value={propertyMasterResult.status}
                />
                <Divider/>
                <LocationVerificationTextField label='Street' value={propertyMasterResult.cleanAddress} tooltip />
                <Divider/>
                <LocationVerificationTextField label='City' value={propertyMasterResult.city} />
                <Divider/>
                <LocationVerificationTextField label='County' value={propertyMasterResult.county} />
                <Divider/>
                <LocationVerificationTextField label='State' value={propertyMasterResult.state} />
                <Divider/>
                <LocationVerificationTextField label='Zip Code'
                  value={propertyMasterResult.zip}
                />
                <Divider/>
                <LocationVerificationTextField label='Latitude' effectName={hasLatitudeChanged ? effectNameRef.current : ''} value={propertyMasterResult.latitude} error={!propertyMasterResult.latitude || !currentGeoResultDto?.isLatLngValid}/>
                <Divider/>
                <LocationVerificationTextField label='Longitude' effectName={hasLongitudeChanged ? effectNameRef.current : ''} value={propertyMasterResult.longitude} error={!propertyMasterResult.longitude || !currentGeoResultDto?.isLatLngValid}/>
                <Divider/>
                <LocationVerificationTextField label='PM ID' value={propertyMasterResult.asgPropID ?? 'Not found'} />
                <Divider/>
                <LocationVerificationTextField label='Has Units' value={propertyMasterResult.hasUnits != null ? propertyMasterResult.hasUnits.toString() : ''} />
                <Divider/>
                <LocationVerificationTextField label='Geocoded By' value={propertyMasterResult.geoCodedBy} />
                <Divider/>
                <LocationVerificationTextField label='Match Quality' value={propertyMasterResult.geocodeMatchQuality} />
                <Divider/>
                <LocationVerificationTextField label='Provider' value={propertyMasterResult.provider} />
              </div>
              {/* <Divider/> */}
              <div className={classes.buttonDiv}>
                <Button variant="contained" color="primary" onClick={handleSaveLocation} disabled={ !currentGeoResultDto?.isLatLngValid || pmSearchIsLoading || !(currentGeoResultDto.passedValidation || (currentGeoResultDto.onlyMissingLatLong && propertyMasterResult.latitude && propertyMasterResult.longitude))}>Use Location</Button>
              </div>
            </div>
            <div style={{ width: 800, display: 'flex', flexDirection: 'column' }}>
              <LocationVerificationMap
                currentPin={{
                  lat: propertyMasterResult.latitude,
                  lng: propertyMasterResult.longitude,
                  maintainZoom: propertyMasterResult.maintainZoom
                }}
                updateLatLng={updateLatLng}
                classes={classes}
              />
              <LocationVerificationGrid nearMatches={propertyMasterResult.nearMatches} searchWithPm={searchWithPm}/>
            </div>
          </div>
          </LoadingOverlayWrapper>
        </DialogContent>
        <LocationVerificationCreatePanel
          open={createModeEnabled}
          geoValidationDto={geoValidationDto}
          onClose={() => setCreateModeEnabled(false)}
          updateLocation={handleSaveLocation}
          onParentClose={onClose}
          setCurrentGeoResultDto={setCurrentGeoResultDto}
          jitCreationType={jitCreationType}
        />
      </div>
    </Dialog>
  )
};
const styles = theme => ({
  dialogPaper: {
    overflow: 'hidden',
    height: 'calc(100% - 64px)'
  },
  controlContainer: {
    display: 'flex',
    background: '#fff',
    height: '100%'
  },
  fieldContainer: {
    overflowY: 'auto',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 4,
    padding: '4px 0 0 6px'
  },
  buttonDiv: {
    margin: '15px 0',
  }
});

export default compose(
  withStyles(styles)
)(LocationVerificationModal);